import { CENTRAL_API_BASE_URL } from '../../constants/central-api';

const SESSION_API = CENTRAL_API_BASE_URL + 'session';

//* The boot splash stays up while this call is in flight (`sessionResolving`),
//* so a request that never settles is a permanently stuck app. Cap it — giving
//* up here just falls through to the `/login` guards, exactly like a 401.
const RESTORE_SESSION_TIMEOUT_MS = 10000;

/**
 * Restore the FULL portal session from the httpOnly `tara_session` cookie.
 *
 * Sends ONLY the cookie (`credentials: 'include'`, no `Authorization` header)
 * to `GET /api/v1/session`. The backend authenticates the cookie and re-mints
 * the login payload (`{ access_token, user, agency_api_key }`) — a fresh seller
 * JWT + the agency key + the user — which the boot restore applies via
 * `applySessionFromResponse`. This is what makes BOTH the legacy seller-JWT
 * calls (tara/mcp/*, seller-init, dashboard data) and the agency calls work
 * after a reload, not just the agency identity.
 *
 * Returns the `data` object on success, or `null` when there is no valid cookie
 * (401) or anything fails — the caller then falls through to the `/login`
 * guards. Never throws.
 */
export async function restoreSession(): Promise<any | null> {
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    RESTORE_SESSION_TIMEOUT_MS
  );

  try {
    const response = await fetch(SESSION_API, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok) {
      return null;
    }

    const json = await response.json();

    // ApiResponse envelope: { success, message, data: { access_token, ... } }
    //
    // An INVITED employee has no seller account, so `access_token` is null and
    // only `agency_api_key` comes back. Requiring the seller JWT here dropped
    // that session on every reload — accept either credential.
    if (
      json?.success === true &&
      (json?.data?.access_token || json?.data?.agency_api_key)
    ) {
      return json.data;
    }

    return null;
  } catch {
    // Includes the abort above — no session restored, guards take over.
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
}
